// Browser-side cleaning for template HTML the website renders or puts in the
// editor (AI output, the live preview). Mirrors the server's rules in
// utils/emailHtml.js, minus class names, so nothing on the page can be
// restyled. DOMParser builds the tree without running scripts or loading
// images, so cleaning never executes the markup it cleans.

const ALLOWED_TAGS = new Set([
	'div', 'span', 'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a', 'img', 'blockquote',
	'table', 'tr', 'td', 'th', 'tbody', 'thead', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
])
// Removed along with everything inside them rather than unwrapped.
const DROPPED_TAGS = new Set([
	'script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math',
	'form', 'textarea', 'select', 'button', 'head', 'title', 'link', 'meta', 'base',
])
const ALLOWED_ATTRIBUTES = {
	a: ['href', 'target', 'style'],
	img: ['src', 'alt', 'width', 'height', 'style'],
	div: ['style'],
	span: ['style'],
	p: ['style'],
	table: ['style', 'border', 'cellpadding', 'cellspacing'],
	td: ['style', 'colspan', 'rowspan'],
	th: ['style', 'colspan', 'rowspan'],
}
const ALLOWED_STYLES = ['color', 'background-color', 'font-size', 'font-weight', 'text-align', 'padding', 'margin', 'line-height']
const SAFE_URL = /^(https?:|mailto:)/i

// Browsers ignore whitespace and control characters inside a URL scheme, so
// "java\tscript:" still runs; drop them before checking.
const withoutBlanks = value => [...value].filter(character => character.charCodeAt(0) > 32).join('')

const cleanStyle = element => {
	const kept = ALLOWED_STYLES.map(property => [property, element.style.getPropertyValue(property)]).filter(
		([, value]) => value && !/url\(|expression/i.test(value),
	)
	if (kept.length) element.setAttribute('style', kept.map(([property, value]) => `${property}: ${value}`).join('; '))
	else element.removeAttribute('style')
}

const cleanAttributes = (element, tag) => {
	for (const attribute of [...element.attributes]) {
		const name = attribute.name.toLowerCase()
		if (!ALLOWED_ATTRIBUTES[tag]?.includes(name)) {
			element.removeAttribute(attribute.name)
		} else if (name === 'href' || name === 'src') {
			if (!SAFE_URL.test(withoutBlanks(attribute.value))) element.removeAttribute(attribute.name)
		} else if (name === 'style') {
			cleanStyle(element)
		}
	}
	if (tag === 'a') element.setAttribute('rel', 'noopener noreferrer')
}

const cleanChildren = parent => {
	for (const node of [...parent.childNodes]) {
		if (node.nodeType === Node.TEXT_NODE) continue
		if (node.nodeType !== Node.ELEMENT_NODE) {
			node.remove()
			continue
		}
		const tag = node.tagName.toLowerCase()
		if (DROPPED_TAGS.has(tag)) {
			node.remove()
			continue
		}
		cleanChildren(node)
		if (ALLOWED_TAGS.has(tag)) cleanAttributes(node, tag)
		else node.replaceWith(...node.childNodes)
	}
}

export const cleanEmailHtml = html => {
	const document = new DOMParser().parseFromString(String(html || ''), 'text/html')
	cleanChildren(document.body)
	return document.body.innerHTML
}
